import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { Box } from "@whoosh/design-system/src/components/primitive";

import { ClubStackNavigatorParamList } from "../../navigators/ClubNavigator";
import { DiningAgendaScreen } from "../AgendaScreen/AgendaScreens/DiningAgendaScreen/DiningAgendaScreen";
import { ExistingDiningBooking } from "../BookingDetail/ForYouDiningRemains/components/DiningAvailability";
import { MoveDiningBookingScreenQuery } from "./__generated__/MoveDiningBookingScreenQuery.graphql";

type MoveDiningBookingRoute = RouteProp<
  ClubStackNavigatorParamList,
  "MoveDiningBooking"
>;

export const MoveDiningBookingScreen = () => {
  const [queryRef, loadQuery] = useQueryLoader<MoveDiningBookingScreenQuery>(
    MoveDiningBookingScreenQueryString
  );

  const route = useRoute<MoveDiningBookingRoute>();
  const { bookingId, date } = route.params;

  useEffect(() => {
    if (bookingId) {
      loadQuery({ bookingId }, { fetchPolicy: "network-only" });
    }
  }, [bookingId, date, loadQuery]);

  return (
    <Suspense fallback={null}>
      {queryRef ? (
        <MoveDiningBookingScreenInternal queryRef={queryRef} />
      ) : null}
    </Suspense>
  );
};

type InternalProps = {
  queryRef: PreloadedQuery<MoveDiningBookingScreenQuery>;
};

export const MoveDiningBookingScreenInternal = ({
  queryRef,
}: InternalProps) => {
  const data = usePreloadedQuery(MoveDiningBookingScreenQueryString, queryRef);

  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const bookingNode = data.node;

  if (bookingNode == null || bookingNode.__typename !== "DiningBooking") {
    // If we somehow fail to load the dining booking, just go back.
    if (navigation.canGoBack()) {
      navigation.goBack();
    }

    return null;
  }

  const agendaDate = bookingNode.startSlot?.agenda?.date ?? null;
  const startTime = bookingNode.startSlot?.time ?? null;

  const playerIds: string[] =
    bookingNode.rounds?.edges
      ?.map((edge: any) => edge?.node?.player?.id ?? null)
      .filter((id: string | null): id is string => Boolean(id)) ?? [];

  const ownerClientId: string | null =
    bookingNode.ownerClient?.__typename === "Member"
      ? bookingNode.ownerClient.id ?? null
      : bookingNode.ownerClient?.__typename === "Customer"
      ? bookingNode.ownerClient.id
      : null;

  const ownerId: string | null =
    ownerClientId ?? (playerIds.length > 0 ? playerIds[0] : null);

  const otherPatronIds: string[] =
    ownerId != null
      ? playerIds.filter((id: string) => id !== ownerId)
      : playerIds;

  const existingBooking: ExistingDiningBooking = {
    id: bookingNode.id,
    roomName: bookingNode.roomName ?? "",
    date: agendaDate ?? "",
    time: startTime ?? "",
    partySize: bookingNode.partySize ?? 0,
    externalId: bookingNode.externalId ?? "",
    notesFromMember: bookingNode.notesFromMember ?? null,
    dietaryTags: bookingNode?.dietaryTags ? [...bookingNode.dietaryTags] : null,
    occasionTags: bookingNode?.occasionTags
      ? [...bookingNode.occasionTags]
      : null,
    ownerId,
    otherPatronIds,
  };

  return (
    <Box flex={1} bg="surface-primary">
      <DiningAgendaScreen existingBooking={existingBooking} />
    </Box>
  );
};

const MoveDiningBookingScreenQueryString = graphql`
  query MoveDiningBookingScreenQuery($bookingId: ID!) {
    node(id: $bookingId) {
      __typename
      ... on DiningBooking {
        id
        roomName
        partySize
        externalId
        notesFromMember
        dietaryTags
        occasionTags
        ownerClient {
          __typename
          ... on Member {
            id
          }
          ... on Customer {
            id
          }
        }
        rounds {
          edges {
            node {
              player {
                id
              }
            }
          }
        }
        startSlot {
          ... on BucketTimeSlot {
            time
          }
          agenda {
            date
          }
        }
      }
    }
  }
`;
